import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Divider, 
  IconButton, 
  Tooltip,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import { 
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import PDFViewer from './PDFViewer';

// Mock OCR function (would normally use Tesseract.js)
const performOCR = async (file: File): Promise<string> => {
  // Simulate OCR processing time
  return new Promise((resolve) => {
    setTimeout(() => {
      // Return mock text content based on file name
      const fileName = file.name.toLowerCase();
      if (fileName.includes('contract')) {
        resolve(`CONTRACT AGREEMENT

This Contract Agreement ("Agreement") is entered into as of [Date], by and between:

[Party A Name], a [type of entity] with its principal place of business at [Address] ("Party A"), and
[Party B Name], a [type of entity] with its principal place of business at [Address] ("Party B").

1. SCOPE OF WORK
Party A agrees to provide the following services to Party B:
• [Description of services]
• [Description of deliverables]
• [Timeline for completion]

2. PAYMENT TERMS
Party B agrees to pay Party A the total sum of [Amount] for the services described in Section 1.
• Initial payment of [Amount] due upon signing this Agreement
• [Payment schedule details]
• Final payment due upon completion of all deliverables

3. TERM AND TERMINATION
This Agreement shall commence on [Start Date] and continue until [End Date], unless terminated earlier as provided herein.
Either party may terminate this Agreement with [Number] days' written notice in the event of a material breach by the other party.

4. CONFIDENTIALITY
Both parties agree to maintain the confidentiality of any proprietary information shared during the course of this Agreement.

5. INTELLECTUAL PROPERTY
All intellectual property created by Party A in the performance of this Agreement shall be owned by [Party A/Party B].

6. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of [State/Country].

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the date first above written.

Party A: ________________________     Party B: ________________________

Date: __________________________     Date: __________________________`);
      } else if (fileName.includes('agreement')) {
        resolve(`SERVICE AGREEMENT

This Service Agreement (the "Agreement") is made and entered into as of [Date] (the "Effective Date"), by and between [Service Provider Name], with a principal place of business at [Address] ("Provider"), and [Client Name], with a principal place of business at [Address] ("Client").

WHEREAS, Provider is in the business of providing [type of services] services; and
WHEREAS, Client wishes to engage Provider to provide certain services as set forth herein.

NOW, THEREFORE, in consideration of the mutual covenants and agreements contained herein, the parties agree as follows:

1. SERVICES
Provider agrees to perform the following services for Client (the "Services"):
• [Detailed description of services]
• [Deliverables]
• [Timeline]

2. COMPENSATION
Client agrees to compensate Provider for the Services as follows:
• [Fee structure]
• [Payment schedule]
• [Invoicing procedures]

3. TERM
This Agreement shall commence on the Effective Date and shall continue until [End Date], unless terminated earlier pursuant to the terms of this Agreement.

4. TERMINATION
Either party may terminate this Agreement upon [Number] days' written notice to the other party.

5. INDEPENDENT CONTRACTOR
Provider is an independent contractor and not an employee of Client.

6. CONFIDENTIALITY
Provider agrees to maintain the confidentiality of Client's proprietary information.

7. GOVERNING LAW
This Agreement shall be governed by the laws of [State/Country].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.

[Service Provider Name]                [Client Name]

By: _________________________        By: _________________________

Name: _______________________        Name: _______________________

Title: ________________________       Title: ________________________`);
      } else {
        resolve(`SAMPLE DOCUMENT

Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl.

Section 1: Introduction
This document serves as a sample for OCR text extraction. The actual content would be derived from the PDF file uploaded by the user.

Section 2: Purpose
The purpose of this document is to demonstrate the functionality of the OCR-based PDF editor component.

Section 3: Features
• Direct text editing
• Paragraph formatting
• Document structure preservation
• Export to PDF

Section 4: Implementation
The implementation uses OCR technology to convert PDF content to editable text, which can then be modified by the user.

Section 5: Conclusion
This approach provides a more practical solution for editing PDF documents, especially for contract management and legal document processing.`);
      }
    }, 3000); // Simulate 3 seconds of processing
  });
};

interface OCRPDFEditorProps {
  file?: File;
  documentId?: string;
  onSave?: (data: any) => Promise<void>;
}

const OCRPDFEditor: React.FC<OCRPDFEditorProps> = ({
  file,
  documentId,
  onSave
}) => {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const [editedText, setEditedText] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'original' | 'edited'>('original');
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Set PDF file when prop changes
  useEffect(() => {
    if (file) {
      console.log(`OCRPDFEditor received file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
      
      // Validate that it's actually a PDF file
      if (file.type === 'application/pdf') {
        setPdfFile(file);
        processFile(file);
      } else {
        console.error(`File is not a PDF: ${file.type}`);
        setError(`Invalid file type: ${file.type}. Only PDF files are supported.`);
        setPdfFile(null);
      }
    } else {
      console.log('OCRPDFEditor: No file provided');
      setPdfFile(null);
    }
  }, [file]);

  // Process the PDF file with OCR
  const processFile = async (file: File) => {
    setIsProcessing(true);
    setError(null);
    setProcessingProgress(0);
    
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Perform OCR on the PDF
      const text = await performOCR(file);
      
      clearInterval(progressInterval);
      setProcessingProgress(100);
      
      // Set the extracted and edited text
      setExtractedText(text);
      setEditedText(text);
      
      setTimeout(() => {
        setIsProcessing(false);
      }, 500);
    } catch (err) {
      console.error('Error processing PDF:', err);
      setError(`Failed to process PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsProcessing(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!onSave) return;
    
    setSaving(true);
    try {
      await onSave({
        id: documentId,
        content: editedText,
        originalContent: extractedText
      });
      setError(null);
    } catch (err) {
      console.error('Error saving document:', err);
      setError(`Failed to save document: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(editedText)
      .then(() => {
        // Show success message (could use a snackbar here)
        console.log('Text copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy text:', err);
      });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {pdfFile ? pdfFile.name : 'Document Editor'}
        </Typography>
        
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || isProcessing}
            sx={{ mr: 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleCopyToClipboard}
            disabled={isProcessing || !editedText}
            sx={{ mr: 1 }}
          >
            Copy Text
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={isProcessing || !editedText}
            sx={{ mr: 1 }}
          >
            Export PDF
          </Button>
        </Box>
      </Box>
      
      {/* Processing indicator */}
      {isProcessing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Processing document with OCR...
          </Typography>
          <LinearProgress variant="determinate" value={processingProgress} />
        </Box>
      )}
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Main content */}
      <Grid container spacing={2}>
        {/* Original PDF view */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Original Document
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            {pdfFile ? (
              <Box sx={{ 
                height: 'calc(100vh - 250px)', 
                border: '1px solid #ddd', 
                borderRadius: 1,
                overflow: 'auto'
              }}>
                <PDFViewer file={pdfFile} scale={1.0} />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="text.secondary">
                  No document loaded
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Editable text view */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Editable Text
              </Typography>
              
              <Tooltip title="Edit text directly">
                <IconButton color="primary">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {isProcessing ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 250px)">
                <CircularProgress />
              </Box>
            ) : (
              <TextField
                multiline
                fullWidth
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                variant="outlined"
                placeholder="Extracted text will appear here"
                disabled={isProcessing}
                inputRef={editorRef}
                InputProps={{
                  sx: {
                    height: 'calc(100vh - 250px)',
                    '& .MuiInputBase-input': {
                      height: '100%',
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }
                  }
                }}
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OCRPDFEditor; 